import {css} from 'aphrodite';
import React from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {Condition} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import getTestProps from 'test/lib/cypress/getTestProps';

import {
  getConditionValidationTooltip,
  NON_UNIFORM_PLAN_CONDITIONS_SAVE_TOOLTIP,
  validateConditions,
} from '../design-state/procedureRunStudyValidationUtils';
import PublishedProcedureVersionUpdateBanner from '../procedure-selection/PublishedProcedureVersionUpdateBanner';
import {VersionUpdateProps} from './ProcedureRunPlanDesignContentTypes';
import {
  StudyDesignTabEditState,
  StudyDesignTabSaveButton,
  StudyDesignTabSavingIndication,
} from './StudyDesignTabSavingButtons';
import {studyDesignTabStyles} from './StudyDesignTabSharedComponents';

interface PlanDesignHeaderProps {
  conditions: Array<Condition> | null;
  editState: StudyDesignTabEditState;
  onSave: () => void;
  procedureType: ProcedureType;
  versionUpdate: VersionUpdateProps;
}

function legacyPlanSaveButtonState(
  conditions: Array<Condition> | null,
  hasUnsavedChanges: boolean
): {isSaveDisabled: boolean; saveDisabledTooltip: string | undefined} {
  if (conditions == null) {
    return {isSaveDisabled: true, saveDisabledTooltip: NON_UNIFORM_PLAN_CONDITIONS_SAVE_TOOLTIP};
  }
  if (!validateConditions(conditions)) {
    return {
      isSaveDisabled: true,
      saveDisabledTooltip: getConditionValidationTooltip(conditions) ?? undefined,
    };
  }
  if (!hasUnsavedChanges) {
    return {isSaveDisabled: true, saveDisabledTooltip: 'No changes to save'};
  }
  return {isSaveDisabled: false, saveDisabledTooltip: undefined};
}

export default function LegacyProcedureRunPlanDesignSubHeader({
  conditions,
  editState,
  onSave,
  procedureType,
  versionUpdate,
}: PlanDesignHeaderProps): JSX.Element {
  const {isSaveDisabled, saveDisabledTooltip} = legacyPlanSaveButtonState(
    conditions,
    versionUpdate.hasUnsavedChanges
  );
  return (
    <div
      className={css(studyDesignTabStyles.header, studyDesignTabStyles.planHeader)}
      {...getTestProps({component: 'LegacyProcedureRunPlanDesignSubHeader', element: 'root'})}
    >
      <div className={css(studyDesignTabStyles.tabTitle)}>Design</div>
      <div className={css(studyDesignTabStyles.bannerAndButtons)}>
        {versionUpdate.hasNewerVersion && (
          <PublishedProcedureVersionUpdateBanner
            procedureType={procedureType}
            onOpenUpdateModal={versionUpdate.onOpenUpdateModal}
            hasUnsavedChanges={versionUpdate.hasUnsavedChanges}
          />
        )}
        {editState === StudyDesignTabEditState.SAVING ? (
          <StudyDesignTabSavingIndication />
        ) : (
          <StudyDesignTabSaveButton
            isSaveDisabled={isSaveDisabled}
            saveDisabledTooltip={saveDisabledTooltip}
            onSave={onSave}
          />
        )}
      </div>
    </div>
  );
}
